import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';

import { AttributeValues } from '@connect-services/client/components/entity-attributes-input/entity-attributes-input.component';
import { EntityTableDateCellComponent } from '@connect-services/client/components/entity-table-date-cell/entity-table-date-cell.component';
import { EntityTableDateHourCellComponent } from '@connect-services/client/components/entity-table-date-hour-cell/entity-table-date-hour-cell.component';
import {
    LayerControllerGetEntities200ResponseAllOfItemsInner as DataEntityAdditionalProperties,
    LayerWithAttributes,
    StandardizedAttributeResponse,
} from '@connect-services/client/sdk/api';
import { AttributeValidationService } from '@connect-services/client/services/attribute-validation.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EditModeInputEventFacadeService } from '@connect-services/client/services/edit-mode-input-event-facade.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { CreatedEntityTableRow } from '@connect-services/client/services/entity-created-table.service';
import { ProjectEntityTableService } from '@connect-services/client/services/project-entity-table.service';
import { EN_DATE_FORMAT, TranslateService } from '@connect-services/client/services/translate.service';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { CustomColumn } from '@connect-services/src/lib/components/table/table.component';
import { InformationSchemaTableExtended } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

const DISABLED_COLUMN_KEYS = ['businessIdentifier'];
const READONLY_COLUMN_KEYS = ['deposit_id', 'uuid'];

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EntityTableDateCellComponent, EntityTableDateHourCellComponent, RessourcesModule, SelectComponent],
    selector: 'td[app-entity-table-cell]',
    standalone: true,
    styleUrl: './entity-table-cell.component.scss',
    templateUrl: './entity-table-cell.component.html',
})
export class EntityTableCellComponent implements OnInit {
    public readonly attributeInfo = input<InformationSchemaTableExtended | null>(null);
    public readonly businessIdentifierKey = input<string>('uuid');
    public readonly classList = computed(() => {
        const classes = ['entity-table-cell-inner'];

        if (this.isDisabled()) {
            classes.push('entity-table-cell-inner--disabled');
        }

        if (this.isCellEditActive() && !this.effectiveReadonly()) {
            classes.push('entity-table-cell-inner--editable');
        }

        if (this.isEdited()) {
            classes.push('entity-table-cell-inner--edited');
        }

        if (!this.isDisabled() && this.isReadonly()) {
            classes.push('entity-table-cell-inner--readonly');
        }

        return classes.join(' ');
    });
    public readonly column = input.required<CustomColumn>();
    public readonly columnKey = computed(() => this.column().key);
    public readonly columnNamesWithReference = input<string[]>([]);
    public readonly dataEntity = input.required<DataEntityAdditionalProperties | CreatedEntityTableRow>();
    public readonly displayValue = computed<unknown>(() => {
        return this.formatValue(this.rawValue());
    });
    public readonly effectiveReadonly = computed(() => {
        if (this.isDisabled()) {
            return true;
        }

        return this.isReadonly();
    });
    public readonly entityId = computed(() => this.dataEntity().uuid);
    public readonly errorMessages = computed(() => this.errors());
    public readonly hasErrors = computed(() => !!this.errors().length);
    public readonly inputMode = computed(() => (this.isNumeric() ? 'decimal' : null));
    public readonly isCellEditActive = input<boolean>(false);
    public readonly isDate = computed(() => {
        return this.attributeInfo()?.dataType === InformationSchemaTableExtended.DataTypeEnum.DATE;
    });
    public readonly isDateAndHour = computed(() => {
        return this.attributeInfo()?.dataType === InformationSchemaTableExtended.DataTypeEnum.TIMESTAMPTZ;
    });
    public readonly isDisabled = computed(() => {
        if (this.isReadonly()) {
            return false;
        }

        if (this.isPendingDelete()) {
            return true;
        }

        return DISABLED_COLUMN_KEYS.includes(this.columnKey());
    });
    public readonly isEdited = computed(() => {
        const originalValue = this.getRawValue(this.dataEntity(), this.columnKey());
        const currentValue = this.rawValue();

        return !this.isSameValue(originalValue, currentValue);
    });
    public readonly isEditSessionActive = input<boolean>(false);
    public readonly isInlineTableEditActive = input<boolean>(false);
    public readonly isNumeric = input<boolean>(false);
    public readonly isPendingDelete = input<boolean>(false);
    public readonly isPendingDeleteInline = input<boolean>(false);
    public readonly isReadonly = computed(() => {
        const columnKey = this.columnKey();

        return READONLY_COLUMN_KEYS.includes(columnKey);
    });
    public readonly layerId = input.required<string>();
    public readonly placeholder = computed(() => {
        if (!this.hasErrors()) {
            return '';
        }

        if (this.isNumeric()) {
            return 'NaN';
        }

        return '';
    });
    public readonly projectId = input.required<string>();
    public readonly rawValue = computed<unknown>(() => {
        const columnKey = this.columnKey();

        this.editModeStateService.getStateTick();

        if (columnKey === 'businessIdentifier') {
            return this.getBusinessIdentifierValue(this.dataEntity());
        }

        if (!this.projectId() || !this.layerId()) {
            return this.getRawValue(this.dataEntity(), columnKey);
        }

        const edited = this.editModeStateService.getEditedProperty(
            this.projectId(),
            this.layerId(),
            this.dataEntity().uuid,
            columnKey,
        );

        if (edited !== undefined) {
            return edited;
        }

        return this.getRawValue(this.dataEntity(), columnKey);
    });

    protected readonly attributeOptions = signal<SelectOption<string>[]>([]);
    protected dateFormatPlaceholder: string = EN_DATE_FORMAT;
    protected defaultAttributeOption: SelectOption<unknown>[] = [];
    protected isAttributeValuesAlreadyFetch = false;
    protected readonly isReferredLayerNotFound = signal<boolean>(false);
    protected readonly layer = computed<LayerWithAttributes | undefined>(() =>
        this.projectEntityTableService.activeLayers().find((layer) => layer.id === this.layerId()),
    );
    protected readonly optionLoading = signal<boolean>(false);
    protected readonly selectedAttributeOption = signal<SelectOption<unknown>[]>([]);

    private readonly attributeValidationService = inject(AttributeValidationService);
    private readonly draftValue = signal<unknown | undefined>(undefined);
    private readonly editEntityDetailService = inject(EditEntityDetailService);
    private readonly editEventFacade = inject(EditModeInputEventFacadeService);
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly errors = signal<string[]>([]);
    private hasNextPage: boolean = false;
    private nextPage = 1;
    private readonly projectEntityTableService = inject(ProjectEntityTableService);
    private searchValue: string | undefined = undefined;
    private readonly translateService = inject(TranslateService);

    public constructor() {
        this.dateFormatPlaceholder = this.translateService.getDateFormat();

        effect(() => {
            this.setSelectedAttributeOption(this.rawValue());
        });
    }

    public ngOnInit(): void {
        if (!this.columnNamesWithReference().includes(this.columnKey())) {
            return;
        }

        this.setSelectedAttributeOption(this.displayValue());
        this.defaultAttributeOption = this.selectedAttributeOption();
    }

    public onCommit(event: Event): void {
        if (!this.isCellEditActive() || this.isReadonly() || this.hasErrors()) {
            return;
        }

        const input = event.target as HTMLInputElement | null;

        if (!input) {
            return;
        }

        const newValue = this.draftValue() ?? this.normalizeInputValue(input.value);

        this.saveEdit(newValue);
    }

    public onInput(event: Event): void {
        if (!this.isCellEditActive() || this.isReadonly()) {
            return;
        }

        const input = event.target as HTMLInputElement | null;
        if (!input) {
            return;
        }

        this.setValueAndErrorsMessage(input.value);
    }

    public pushEdit(newValue: unknown): void {
        const projectId = this.projectId();
        const layerId = this.layerId();
        const layer = this.layer();
        if (!projectId || !layerId || !layer?.tableName) {
            return;
        }

        const entity = this.dataEntity();
        const attributeKey = this.columnKey();
        const previousValue = this.rawValue();

        if (this.isSameValue(previousValue, newValue)) {
            return;
        }

        const businessIdentifier = { key: this.businessIdentifierKey(), value: entity[this.businessIdentifierKey()] };

        this.editEventFacade.addOnePropertyUpdate({
            attributeKey,
            businessIdentifier,
            entityId: entity.uuid,
            layerId,
            newValue,
            previousValue,
            projectId,
            tableName: layer.tableName,
        });
    }

    protected async fetchNextPage(): Promise<void> {
        if (!this.hasNextPage) {
            return;
        }

        const attributeValues = await this.fetchAttributeValues(this.nextPage, this.searchValue);

        if (!attributeValues) {
            return;
        }

        this.nextPage = this.nextPage + 1;

        this.attributeOptions.update((currentAttributeValues) => {
            return [
                ...currentAttributeValues,
                ...attributeValues.values.map((label: string) => {
                    return { label, value: label };
                }),
            ];
        });
    }

    protected async initAttributeValues(): Promise<void> {
        if (this.isAttributeValuesAlreadyFetch) {
            return;
        }

        this.isAttributeValuesAlreadyFetch = true;

        await this.setAttributeValuesFirstPage();
    }

    protected resetStandardizedAttributeValue(): void {
        this.setValueAndErrorsMessage(null);

        if (this.errorMessages().length) {
            return;
        }

        this.selectedAttributeOption.set([]);
        this.saveEdit(null);
    }

    protected async search(value: string): Promise<void> {
        this.searchValue = value;
        this.nextPage = 1;

        await this.setAttributeValuesFirstPage();
    }

    protected setStandardizedAttributesValue(value: unknown): void {
        if (!value) {
            return;
        }

        this.setValueAndErrorsMessage(value ?? null);

        if (this.errorMessages().length) {
            return;
        }

        this.setSelectedAttributeOption(value ?? null);

        this.saveEdit(value);
    }

    private async fetchAttributeValues(page: number, searchValue?: string): Promise<AttributeValues | null> {
        this.optionLoading.set(true);

        const layer = this.layer();
        if (!layer) {
            return null;
        }

        const attributeValues = await this.editEntityDetailService.getAttributesValue(
            this.columnKey(),
            layer,
            page,
            searchValue,
        );
        this.hasNextPage = attributeValues.hasNextPage;
        this.isReferredLayerNotFound.set(
            attributeValues.errorKey === StandardizedAttributeResponse.ErrorKeyEnum.REFERRED_LAYER_NOT_FOUND,
        );
        this.optionLoading.set(false);

        return attributeValues;
    }

    private formatValue(value: unknown): unknown {
        if (typeof value !== 'string') {
            return value;
        }

        switch (this.attributeInfo()?.dataType) {
            case InformationSchemaTableExtended.DataTypeEnum.DATE:
                return this.translateService.getFormattedLocaleDate(new Date(value));
            case InformationSchemaTableExtended.DataTypeEnum.TIMESTAMPTZ:
                return this.translateService.getFormattedLocaleDate(
                    new Date(value),
                    this.translateService.getDateHourFormat(),
                );
            default:
                return value;
        }
    }

    private getBusinessIdentifierValue(entity: DataEntityAdditionalProperties | CreatedEntityTableRow): string {
        const businessIdentifierKey = this.businessIdentifierKey();

        return entity[businessIdentifierKey] ?? entity.businessIdentifier;
    }

    private getRawValue(
        entity: DataEntityAdditionalProperties | CreatedEntityTableRow,
        columnKey: keyof DataEntityAdditionalProperties,
    ): unknown {
        if (columnKey === 'deposit_id') {
            const depositsMap = this.projectEntityTableService.deposits();
            const depositId = entity[columnKey];

            if (!depositId) {
                return depositId;
            }

            const deposit = depositsMap.get(depositId);
            if (!deposit) {
                return depositId;
            }

            return `${deposit.depositFolder.name} (v${deposit.version})`;
        }

        return entity[columnKey];
    }

    private isSameDate(previousValue: unknown, newValue: unknown): boolean {
        const previousDate = this.parseDate(previousValue);
        const newDate = this.parseDate(newValue);

        if (!previousDate || !newDate) {
            return false;
        }

        return previousDate.getTime() === newDate.getTime();
    }

    private isSameNumber(previousValue: unknown, newValue: unknown): boolean {
        const prev = Number(previousValue);
        const next = Number(newValue);

        if (Number.isNaN(prev) || Number.isNaN(next)) {
            return false;
        }

        return prev === next;
    }

    private isSameValue(previousValue: unknown, newValue: unknown): boolean {
        previousValue = this.normalizeNullish(previousValue);
        newValue = this.normalizeNullish(newValue);

        if (previousValue === newValue) {
            return true;
        }

        if (previousValue == null || newValue == null) {
            return previousValue == null && newValue == null;
        }

        if (this.isDate()) {
            return this.isSameDate(previousValue, newValue);
        }

        if (this.isNumeric()) {
            return this.isSameNumber(previousValue, newValue);
        }

        return false;
    }

    private normalizeInputValue(raw: unknown): unknown {
        if (!raw) {
            return null;
        }

        if (!this.isNumeric()) {
            return raw;
        }

        const parsed = Number(raw);

        return Number.isNaN(parsed) ? raw : parsed;
    }

    private normalizeNullish(value: unknown): unknown {
        return value === '' || value === undefined ? null : value;
    }

    private parseDate(value: unknown): Date | null {
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        if (typeof value === 'string') {
            const parsed = new Date(value);

            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        return null;
    }

    private saveEdit(newValue: unknown): void {
        const currentValue = this.rawValue();
        if (this.isSameValue(currentValue, newValue)) {
            this.draftValue.set(undefined);

            return;
        }

        this.pushEdit(newValue);
        this.draftValue.set(undefined);
    }

    private async setAttributeValuesFirstPage(): Promise<void> {
        const attributeValues = await this.fetchAttributeValues(0, this.searchValue);

        if (!attributeValues) {
            return;
        }

        this.attributeOptions.set(
            attributeValues.values.map((label: string) => {
                return { label, value: label };
            }),
        );
    }

    private setSelectedAttributeOption(value?: unknown): void {
        if (!value) {
            this.selectedAttributeOption.set([]);

            return;
        }

        this.selectedAttributeOption.set([{ label: String(value), value }]);
    }

    private setValueAndErrorsMessage(value: unknown): void {
        const normalized = this.normalizeInputValue(value);
        const newErrors = this.validateValue(normalized);
        this.errors.set(newErrors);

        if (newErrors.length) {
            this.draftValue.set(undefined);

            return;
        }

        this.draftValue.set(normalized);
    }

    private validateValue(value: unknown): string[] {
        if (!this.attributeInfo()) {
            return [];
        }

        const control = this.attributeValidationService.createControl(value, this.attributeInfo()!);

        return this.attributeValidationService.getErrorMessages(control);
    }
}
